import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { CUSTOM_SCROLLBAR, MAX_THREAD_TITLE_LENGTH } from "@/lib/constants";
import { cn, parseServerUtcMs } from "@/lib/utils";
import {
  useChatThreadsQuery,
  useDeleteChatThread,
  useRenameChatThread,
} from "@/service-hooks/use-chat-threads-service";
import { ChatThread } from "@/types/chat";
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Trash2,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import ConfirmDialog from "./confirm-dialog";

// The title cap (MAX_THREAD_TITLE_LENGTH) is enforced via soft validation: the
// input lets the user type/paste anything, but on save (Enter or blur) an
// oversized value gets a toast and the request never goes out. Prefer this
// over `maxLength` here because the user can see the full string they entered
// and knows exactly how much to trim.

type ThreadBucket = "Today" | "Yesterday" | "Previous 7 days" | "Older";
const BUCKET_ORDER: ThreadBucket[] = [
  "Today",
  "Yesterday",
  "Previous 7 days",
  "Older",
];

// Bucket threads by `lastActivityAt` (falling back to updatedAt / createdAt)
// against local-day boundaries. Preserves each bucket's original API order
// (already newest-first from the server) so the sidebar reads top-down as
// "most recent, most recent, …".
function bucketThreads(threads: ChatThread[]): Record<ThreadBucket, ChatThread[]> {
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
  ).getTime();
  const startOfYesterday = startOfToday - 24 * 60 * 60 * 1000;
  const startOf7DaysAgo = startOfToday - 7 * 24 * 60 * 60 * 1000;

  const buckets: Record<ThreadBucket, ChatThread[]> = {
    Today: [],
    Yesterday: [],
    "Previous 7 days": [],
    Older: [],
  };

  for (const t of threads) {
    const ts = parseServerUtcMs(
      t.lastActivityAt || t.updatedAt || t.createdAt,
    );
    if (!Number.isFinite(ts)) {
      buckets.Older.push(t);
      continue;
    }
    if (ts >= startOfToday) buckets.Today.push(t);
    else if (ts >= startOfYesterday) buckets.Yesterday.push(t);
    else if (ts >= startOf7DaysAgo) buckets["Previous 7 days"].push(t);
    else buckets.Older.push(t);
  }
  return buckets;
}

interface ChatThreadsSidebarProps {
  uuid: string;
  agent: string;
  chatMode: string;
  // When set, list only deep-analysis (true) or only regular (false) threads.
  // Lets the deep-analysis toggle swap the sidebar to its own thread list.
  isDeepAnalysis?: boolean;
  activeThreadId?: string;
  onSelectThread: (threadId: string) => void;
  onNewChat: () => void;
}

/**
 * Collapsible left-hand list of the current user's chat threads for
 * (uuid, agent, chatMode), ChatGPT-style. "New chat" leaves the URL without a
 * threadId so the backend mints a fresh thread on the first message
 * (see [[chat-thread.service]] on the backend for auto-title logic).
 */
export default function ChatThreadsSidebar({
  uuid,
  agent,
  chatMode,
  isDeepAnalysis,
  activeThreadId,
  onSelectThread,
  onNewChat,
}: ChatThreadsSidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [renameTarget, setRenameTarget] = useState<ChatThread | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ChatThread | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const renameInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading, isError, hasNextPage, fetchNextPage, isFetchingNextPage } = useChatThreadsQuery(
    uuid,
    agent,
    chatMode,
    true,
    isDeepAnalysis,
  );
  const rename = useRenameChatThread(uuid, agent);
  // Load the next page when the end of the list scrolls into view.
  const moreRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = moreRef.current;
    if (!el || !hasNextPage) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting) && !isFetchingNextPage) fetchNextPage();
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  const del = useDeleteChatThread(uuid, agent);

  useEffect(() => {
    if (renameTarget) {
      setRenameValue(renameTarget.title);
      requestAnimationFrame(() => renameInputRef.current?.select());
    }
  }, [renameTarget]);

  // Belt-and-braces filter: prefer server-side filtering by `chatMode` (query
  // param above), but if the backend hasn't been updated yet we still trim any
  // threads whose stored `chatMode` doesn't match. Threads without a stored
  // `chatMode` (legacy rows) fall through to every mode so we don't hide
  // history the user still expects to see.
  const threads = (data?.data ?? []).filter(
    (t) => !t.chatMode || t.chatMode === chatMode,
  );

  // All validation errors surface as a toast (DEF-05 — the defect is that
  // there was no feedback at all). Enter (`"submit"`) also keeps the input
  // open + refocuses so the user can fix inline; blur closes the editor
  // (they've walked away) after showing the toast.
  const commitRename = (source: "submit" | "blur" = "submit") => {
    if (!renameTarget) return;
    const trimmed = renameValue.trim();
    const failValidation = (message: string) => {
      toast.error(message);
      if (source === "submit") {
        renameInputRef.current?.focus();
        return;
      }
      setRenameTarget(null);
    };
    if (!trimmed) {
      failValidation("Chat name cannot be empty");
      return;
    }
    if (trimmed.length > MAX_THREAD_TITLE_LENGTH) {
      failValidation(
        `Chat name must be ${MAX_THREAD_TITLE_LENGTH} characters or less`,
      );
      return;
    }
    if (trimmed === renameTarget.title) {
      setRenameTarget(null);
      return;
    }
    rename.mutate(
      { threadId: renameTarget.threadId, title: trimmed },
      {
        onSuccess: () => setRenameTarget(null),
        onError: (err) => toast.error(err.message || "Failed to rename thread"),
      },
    );
  };

  if (isCollapsed) {
    return (
      <div className="flex flex-col items-center gap-2 border-r border-surface-border/40 bg-surface-card/40 px-2 py-4">
        <button
          type="button"
          onClick={() => setIsCollapsed(false)}
          className="rounded-md p-1.5 text-muted hover:bg-surface-hover hover:text-white"
          aria-label="Expand chat threads"
          title="Expand threads"
        >
          <ChevronRight className="h-4 w-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          onClick={onNewChat}
          className="rounded-md p-1.5 text-muted hover:bg-surface-hover hover:text-white"
          aria-label="New chat"
          title="New chat"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    );
  }

  const bucketed = bucketThreads(threads);

  const renderThread = (thread: ChatThread) => {
    const isActive = thread.threadId === activeThreadId;
    const isRenaming = renameTarget?.threadId === thread.threadId;
    return (
      <li key={thread.threadId} className="relative">
        {isActive && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-y-1 left-0 w-0.5 rounded-full bg-primary"
          />
        )}
        <div
          className={cn(
            "group flex items-center gap-1 rounded-md pl-3 pr-1.5 py-1.5 text-sm transition-colors",
            isActive
              ? "bg-primary/10 text-white"
              : "text-text-secondary hover:bg-surface-hover hover:text-white",
          )}
        >
          {isRenaming ? (
            <Input
              ref={renameInputRef}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onBlur={() => commitRename("blur")}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitRename("submit");
                } else if (e.key === "Escape") {
                  e.preventDefault();
                  setRenameTarget(null);
                }
              }}
              className="h-7 flex-1 bg-surface-input text-xs text-white"
            />
          ) : (
            <button
              type="button"
              onClick={() => onSelectThread(thread.threadId)}
              className={cn(
                "flex-1 truncate text-left leading-5",
                isActive && "font-medium",
              )}
              title={thread.title}
            >
              {thread.title || "Untitled"}
            </button>
          )}
          {!isRenaming && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    "rounded p-1 text-muted opacity-0 transition-opacity hover:bg-surface-active hover:text-white group-hover:opacity-100 focus-visible:opacity-100",
                    isActive && "opacity-100",
                  )}
                  aria-label="Thread actions"
                >
                  <MoreHorizontal
                    className="h-3.5 w-3.5"
                    aria-hidden="true"
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-surface">
                <DropdownMenuItem
                  className="text-text-primary"
                  onSelect={() => setRenameTarget(thread)}
                >
                  <Pencil
                    className="mr-2 h-3.5 w-3.5"
                    aria-hidden="true"
                  />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuItem
                  className="text-error focus:text-error"
                  onSelect={() => setDeleteTarget(thread)}
                >
                  <Trash2
                    className="mr-2 h-3.5 w-3.5"
                    aria-hidden="true"
                  />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </li>
    );
  };

  return (
    <aside className="flex w-72 flex-col border-r border-surface-border/40 bg-surface-card/40">
      <div className="flex items-center justify-between px-3 py-3">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted">
          Chats
        </span>
        <button
          type="button"
          onClick={() => setIsCollapsed(true)}
          className="rounded-md p-1 text-muted hover:bg-surface-hover hover:text-white"
          aria-label="Collapse chat threads"
          title="Collapse"
        >
          <ChevronLeft className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>

      <div className="px-3 pb-3">
        <Button
          type="button"
          onClick={onNewChat}
          className="w-full justify-start gap-2 bg-primary text-white hover:bg-primary/90"
          size="sm"
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          New chat
        </Button>
      </div>

      <div className={cn("flex-1 overflow-y-auto px-2 pb-3", CUSTOM_SCROLLBAR)}>
        {isLoading && (
          <div className="flex items-center justify-center py-6 text-muted">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        )}
        {isError && (
          <div className="px-2 py-3 text-xs text-error">
            Failed to load threads.
          </div>
        )}
        {!isLoading && !isError && threads.length === 0 && (
          <div className="px-2 py-3 text-xs text-muted">
            No threads yet — start one with New chat.
          </div>
        )}
        {BUCKET_ORDER.map((bucket) => {
          const items = bucketed[bucket];
          if (!items.length) return null;
          return (
            <section key={bucket} className="mb-2">
              <h3 className="px-2 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted/70">
                {bucket}
              </h3>
              <ul className="space-y-px">{items.map(renderThread)}</ul>
            </section>
          );
        })}
        {hasNextPage && (
          <div ref={moreRef} className="flex justify-center py-2">
            {isFetchingNextPage ? (
              <Loader2 className="h-4 w-4 animate-spin text-muted" />
            ) : (
              <button
                type="button"
                onClick={() => fetchNextPage()}
                className="text-xs text-muted hover:text-text-primary"
              >
                Show older threads
              </button>
            )}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
        onConfirm={() => {
          if (!deleteTarget) return;
          const doomed = deleteTarget;
          del.mutate(doomed.threadId, {
            onSuccess: () => {
              // If we just deleted the thread the user is looking at, drop
              // back to the "no thread selected" URL so the chat area resets
              // instead of trying to render history for a missing thread.
              if (doomed.threadId === activeThreadId) {
                onNewChat();
              }
            },
            onError: (err) => toast.error(err.message || "Failed to delete thread"),
          });
        }}
        title="Delete this thread?"
        description="All messages in this thread will be permanently deleted."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </aside>
  );
}
